/**
 * A sweep's report. Backlog tasks 83 and 84.
 *
 * `Backtesting-and-Simulation.md#parameter-sweeps` describes this screen almost line by line, and
 * two of its sentences are requirements rather than suggestions:
 *
 * > **Every sweep result displays the number of configurations tried.** *"The best of 500"* is a
 * > fundamentally different claim from *"this works"*, and the UI must not let me confuse them.
 *
 * > **A plateau is better than a peak.** The report highlights parameter regions that are broadly
 * > good over isolated spikes, because a spike is fitted to noise and a plateau reflects structure.
 *
 * So the denominator is the **first thing on the panel**, at the same type step as the winner, and
 * the caveat is an undismissable band rather than a footnote. Neither is a stylistic choice: the
 * whole danger of a sweep is that its output reads like a measurement, and the layout is what says
 * otherwise before the number is read.
 *
 * ## The best configuration is never the hero
 *
 * `UI-Design-Spec.md`'s *"one hero figure per screen"* is already spent on net return, and that
 * hero is the **baseline** — the version's declared parameters, chosen before this data was
 * searched. The winner sits in the grid beside the denominator that qualifies it. A layout putting
 * the best-of-N where the eye lands first would be Z15 rendered as a screen.
 *
 * ## The heatmap draws executed cells and nothing else
 *
 * An empty cell means no configuration in this slice produced a score — the metric was undefined,
 * or the configuration failed. It is drawn as `—`, which is the design system's mark for absence,
 * and never as a zero. `covered` of `cells` is stated so a mostly-empty surface cannot be read as
 * a smooth one.
 */

import { AttentionCard, AttentionPanel } from '../design/components/messages/AttentionCard.jsx';
import { CautionBand } from '../design/components/messages/CautionBand.jsx';
import { DataTable } from '../design/components/data/DataTable.jsx';
import { EmptyState } from '../design/components/messages/EmptyState.jsx';
import { MetricTile } from '../design/components/data/MetricTile.jsx';
import { Num } from '../design/components/data/Num.jsx';
import type { SweepResponse } from '../api/generated';

/** Absence. `—` is not zero — the design system's own rule, and here it is load-bearing. */
const ABSENT = '—';

type Grid = {
  metric?: string;
  dimensions?: Array<{ name?: string; values?: string[] }>;
  size?: number;
};

type Heatmap = {
  x?: string;
  y?: string;
  x_values?: string[];
  y_values?: string[];
  rows?: Array<Array<string | null>>;
  held_at?: Record<string, string>;
  covered?: number;
  cells?: number;
};

type Sensitivity = {
  centred_on_baseline?: boolean;
  verdict?: string;
  shape?: string;
  positive_share_pct?: string | null;
  reasons?: string[];
  parameters?: Array<{
    name?: string;
    centre_value?: string;
    verdict?: string;
    shape?: string;
    retention_pct?: string | null;
    reason?: string;
    lower?: { value?: string; distance_pct?: string; positive?: boolean } | null;
    upper?: { value?: string; distance_pct?: string; positive?: boolean } | null;
  }>;
};

export function SweepReport({ sweep }: { sweep: SweepResponse }): React.JSX.Element {
  const grid = (sweep.grid ?? null) as Grid | null;
  const best = (sweep.best ?? null) as { parameters?: Record<string, string>; score?: string | null } | null;
  const baseline = (sweep.baseline ?? null) as {
    parameters?: Record<string, string>;
    in_grid?: boolean;
    rank?: number | null;
    ranked_against?: number;
  } | null;
  const sensitivity = (sweep.sensitivity ?? null) as Sensitivity | null;
  const heatmap = (sweep.heatmap ?? null) as Heatmap | null;

  if (sweep.state !== 'done') {
    return (
      <section className="jmt-report-sweep">
        <h2 className="jmt-section-title">Parameter sweep</h2>
        <EmptyState>
          This sweep is {sweep.state}. {sweep.configurations_executed} of{' '}
          {sweep.configurations_tried} configurations have been executed and committed; each one is
          a full event-driven replay, so a large grid is measured in hours.
        </EmptyState>
      </section>
    );
  }

  return (
    <section className="jmt-report-sweep">
      <h2 className="jmt-section-title">Parameter sweep</h2>

      {/*
        Undismissable, and first. The design system built this component for exactly this screen —
        *"the anti-overfitting device … NOT DISMISSABLE, and present on every export and every
        screenshot of the view"* — and the count it takes is the denominator. The message comes from
        the **stored result** rather than from this file, so a second view of the same data cannot
        forget it.
      */}
      <CautionBand
        count={sweep.configurations_tried}
        unit="configurations tried"
        message={sweep.caveat ?? undefined}
      />

      <div className="jmt-report-grid">
        <MetricTile
          label="Configurations tried"
          value={<Num value={String(sweep.configurations_tried)} />}
          context={
            sweep.complete
              ? `all executed · ranked by ${grid?.metric ?? 'net return'}`
              : `${sweep.configurations_executed} executed — this search was cancelled part-way`
          }
        />
        <MetricTile
          label="Best of them"
          value={<Num value={best?.score ?? ABSENT} />}
          context={
            best === null
              ? 'no configuration produced a score'
              : describe(best.parameters ?? {})
          }
        />
        <MetricTile
          label="Declared parameters"
          value={
            <Num
              value={
                baseline?.rank == null ? ABSENT : `#${baseline.rank}`
              }
            />
          }
          context={
            baseline?.in_grid === true
              ? `of ${baseline.ranked_against ?? 0} searched · ${describe(baseline.parameters ?? {})}`
              : 'not on this grid, so it has no rank among the configurations searched'
          }
        />
        <MetricTile
          label="Worked at all"
          value={<Num value={sensitivity?.positive_share_pct == null ? ABSENT : `${trim(sensitivity.positive_share_pct)}%`} />}
          context="share of the grid with positive expectancy"
        />
        <MetricTile
          label="Failed"
          value={<Num value={String(sweep.configurations_failed ?? 0)} />}
          context="configurations whose replay raised"
        />
        <MetricTile
          label="Surface"
          value={<Num value={sensitivity?.shape ?? ABSENT} />}
          context={
            sensitivity?.shape === 'plateau'
              ? 'broadly good — structure rather than noise'
              : sensitivity?.shape === 'peak'
                ? 'an isolated spike, which is the warning'
                : 'neither a plateau nor a spike'
          }
        />
      </div>

      <Sensitivity analysis={sensitivity} />
      <Heat map={heatmap} metric={grid?.metric ?? 'score'} />
      <Configurations sweep={sweep} />
    </section>
  );
}

/** G4, and the per-parameter profile it was read off. */
function Sensitivity({ analysis }: { analysis: Sensitivity | null }): React.JSX.Element {
  if (analysis === null) {
    return (
      <EmptyState>
        No sensitivity analysis was stored for this sweep, which means the run predates it.
      </EmptyState>
    );
  }
  if (analysis.centred_on_baseline !== true) {
    return (
      <div className="jmt-note">
        <h3 className="jmt-section-title">Parameter robustness (G4)</h3>
        <AttentionPanel count={1} empty="">
          <AttentionCard
            severity="attention"
            title="G4 could not be evaluated from this sweep."
            detail={
              (analysis.reasons ?? []).join(' ') ||
              'The declared parameters are not one of the configurations this grid executed.'
            }
          />
        </AttentionPanel>
        <p className="jmt-note-text">
          G4 is a statement about the configuration being promoted. Answering it from the
          best-scoring point instead would measure the robustness of a maximum that had just been
          selected — which is the multiple-comparisons problem answering its own question.
        </p>
      </div>
    );
  }

  const rows = (analysis.parameters ?? []).map((entry) => ({
    id: entry.name ?? '?',
    parameter: entry.name ?? '?',
    at: entry.centre_value ?? ABSENT,
    lower: flank(entry.lower),
    upper: flank(entry.upper),
    retention: entry.retention_pct == null ? ABSENT : `${trim(entry.retention_pct)}%`,
    shape: entry.shape ?? ABSENT,
    verdict: entry.verdict ?? ABSENT,
  }));

  return (
    <div className="jmt-report-sensitivity">
      <h3 className="jmt-section-title">
        Parameter robustness (G4) — {analysis.verdict ?? 'unknown'}
      </h3>
      <DataTable
        columns={[
          { key: 'parameter', label: 'Parameter' },
          { key: 'at', label: 'Declared at', align: 'right' },
          { key: 'lower', label: '−10%', align: 'right' },
          { key: 'upper', label: '+10%', align: 'right' },
          { key: 'retention', label: 'Neighbours keep', align: 'right' },
          { key: 'shape', label: 'Shape' },
          { key: 'verdict', label: 'Verdict' },
        ]}
        rows={rows}
      />
      {(analysis.reasons ?? []).length > 0 ? (
        <AttentionPanel count={(analysis.reasons ?? []).length} empty="">
          {(analysis.reasons ?? []).map((reason, index) => (
            <AttentionCard
              key={index}
              severity={analysis.verdict === 'fail' ? 'critical' : 'attention'}
              title={reason}
            />
          ))}
        </AttentionPanel>
      ) : (
        <p className="jmt-note-text">
          Every parameter keeps a positive expectancy at the nearest grid value at least 10% away
          on each side. The flanks are configurations that were actually executed, never
          interpolations.
        </p>
      )}
    </div>
  );
}

/** *"Results as a heatmap over two parameters."* */
function Heat({ map, metric }: { map: Heatmap | null; metric: string }): React.JSX.Element | null {
  if (map === null || (map.rows ?? []).length === 0) {
    return (
      <p className="jmt-note-text">
        A heatmap needs two axes. This sweep varied one, so the profile above is the whole surface.
      </p>
    );
  }
  const held = Object.entries(map.held_at ?? {});
  return (
    <div className="jmt-report-heatmap">
      <h3 className="jmt-section-title">
        {map.y} against {map.x} — {metric}
      </h3>
      <div style={{ overflowX: 'auto' }}>
        <table className="jmt-heatmap-table">
          <thead>
            <tr>
              <th scope="col">{map.y}</th>
              {(map.x_values ?? []).map((value) => (
                <th key={value} scope="col">
                  {value}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(map.y_values ?? []).map((rowValue, rowIndex) => (
              <tr key={rowValue}>
                <th scope="row">{rowValue}</th>
                {((map.rows ?? [])[rowIndex] ?? []).map((cell, cellIndex) => (
                  <td key={cellIndex}>
                    {/*
                      An empty cell is `—`. Never a zero: no configuration in this slice produced a
                      score, which is a different fact from one that produced nothing.
                    */}
                    <Num value={cell === null ? ABSENT : trim(cell)} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="jmt-note-text">
        {map.covered ?? 0} of {map.cells ?? 0} cells carry a number
        {held.length === 0
          ? '.'
          : `, with ${held.map(([name, value]) => `${name} held at ${value}`).join(' and ')}.`}{' '}
        Held rather than averaged: a mean over a third axis is a surface no configuration ever ran
        at, and a plateau in it can be two peaks either side of a trough.
      </p>
    </div>
  );
}

/** *"Plus a scatter over all runs."* Rendered as the table it is, sorted by score. */
function Configurations({ sweep }: { sweep: SweepResponse }): React.JSX.Element {
  const rows = [...sweep.results]
    .sort((a, b) => Number(b.score ?? '-Infinity') - Number(a.score ?? '-Infinity'))
    .map((result) => ({
      id: result.config_index,
      config: `#${result.config_index}`,
      parameters: describe(result.parameters as Record<string, string>),
      score: result.error === null ? trim(result.score ?? ABSENT) : ABSENT,
      trades: String(result.trade_count),
      note: result.error ?? '',
    }));

  return (
    <div className="jmt-report-configurations">
      <h3 className="jmt-section-title">Every configuration ({rows.length})</h3>
      <DataTable
        columns={[
          { key: 'config', label: 'Config' },
          { key: 'parameters', label: 'Parameters' },
          { key: 'score', label: 'Score', align: 'right' },
          { key: 'trades', label: 'Trades', align: 'right' },
          { key: 'note', label: 'Failure' },
        ]}
        rows={rows}
      />
      <p className="jmt-note-text">
        Failures keep their row. A grid whose corner crashes still searched that corner, and
        dropping it would understate the denominator every overfitting argument rests on.
      </p>
    </div>
  );
}

function flank(
  side: { value?: string; distance_pct?: string; positive?: boolean } | null | undefined,
): string {
  if (side == null) return ABSENT;
  // The verdict is spelled out beside the value rather than carried by colour alone — the design
  // system's rule, and the one that matters most on a table somebody scans.
  return `${side.value ?? ABSENT} ${side.positive === true ? 'holds' : 'fails'}`;
}

function describe(parameters: Record<string, string>): string {
  const entries = Object.entries(parameters);
  if (entries.length === 0) return 'the strategy’s own defaults';
  return entries.map(([name, value]) => `${name} ${value}`).join(', ');
}

/** Two decimals, for prose. The stored value keeps every digit; a table does not need them. */
function trim(value: string): string {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : value;
}
